import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { API } from '../../../../api';
import { getLLAuthHeaders } from '../../utils/llAuthHeaders';
import type { SharedKind } from './capabilitySource';
import { emptySet, setUpdate, setsFromApi, type CapabilitySet } from './sharedSets';

/**
 * The org admin's shared capability sets, Development and Standards
 * (./sharedSets.ts), and how a change to one is kept.
 *
 * `api`: read from and written to the backend (GET /v3/admin/capability-setup/sets,
 * PUT …/sets/{kind}); its answer is the truth. A set's saves go one at a time,
 * each with the version the one before it was answered with.
 *
 * `error`: the read failed: there are no sets to show.
 */
export type SetsMode = 'loading' | 'api' | 'error';

const errorMessage = (err: any, fallback: string) => err?.response?.data?.message || fallback;
const EMPTY: CapabilitySet[] = [emptySet('development'), emptySet('standards')];

export const useCapabilitySets = (orgId: string, isPlatformAdmin: boolean) => {
  const [sets, setSets] = useState<CapabilitySet[]>(EMPTY);
  const [mode, setMode] = useState<SetsMode>('loading');
  const [pending, setPending] = useState<Record<string, number>>({});
  const latest = useRef<CapabilitySet[]>(EMPTY);
  const queues = useRef(new Map<SharedKind, Promise<void>>());
  const alive = useRef(true);
  const forOrg = isPlatformAdmin ? orgId : undefined;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const show = useCallback((next: CapabilitySet[]) => {
    latest.current = next;
    setSets(next);
  }, []);

  const load = useCallback(
    async (background = false) => {
      if (!background) setMode('loading');
      try {
        const res: any = await API.getCapabilitySets({ ...getLLAuthHeaders(), orgId: forOrg });
        if (!alive.current) return;
        const body = res?.data?.data ?? res?.data;
        show(setsFromApi(body?.sets));
        setMode('api');
      } catch {
        if (!alive.current || background) return;
        show(EMPTY);
        setMode('error');
      }
    },
    [forOrg, show]
  );

  useEffect(() => {
    load();
  }, [load]);

  const track = useCallback(
    (kind: SharedKind, step: number) =>
      setPending((current) => {
        const count = (current[kind] ?? 0) + step;
        const { [kind]: _counted, ...rest } = current;
        return count > 0 ? { ...rest, [kind]: count } : rest;
      }),
    []
  );

  /**
   * Saves a change to a set: `change` turns the set as last saved into the set
   * as it should be, when the save goes. Resolves true once saved (or with
   * nothing to save); false when refused (the reason is shown, the sets read again).
   */
  const saveSet = useCallback(
    (kind: SharedKind, change: (set: CapabilitySet) => CapabilitySet): Promise<boolean> => {
      const current = () => latest.current.find((s) => s.kind === kind) ?? emptySet(kind);
      const before = queues.current.get(kind) ?? Promise.resolve();
      const run = before.then(async () => {
        const saved = current();
        const update = setUpdate(saved, change(saved));
        if (!update) return true;
        try {
          const res: any = await API.updateCapabilitySet({
            ...getLLAuthHeaders(),
            orgId: forOrg,
            kind,
            set: { version: saved.version ?? 0, ...update, capabilities: update.capabilities ?? undefined } as any
          });
          const [answer] = setsFromApi([{ ...(res?.data?.data ?? res?.data), kind }]);
          show(latest.current.map((s) => (s.kind === kind && (s.version ?? 0) <= (answer.version ?? 0) ? answer : s)));
          return true;
        } catch (err) {
          toast.error(errorMessage(err, 'That change could not be saved. Check your connection and try again.'));
          load(true);
          return false;
        }
      });
      const done = run.then(
        () => undefined,
        () => undefined
      );
      queues.current.set(kind, done);
      track(kind, 1);
      done.then(() => {
        track(kind, -1);
        if (queues.current.get(kind) === done) queues.current.delete(kind);
      });
      return run;
    },
    [forOrg, show, load, track]
  );

  const isSaving = useCallback((kind: SharedKind) => (pending[kind] ?? 0) > 0, [pending]);
  /** Whether there are shared sets to show and edit: the backend answered with them. */
  const available = mode === 'api';

  return { sets: available ? sets : EMPTY, mode, available, saveSet, isSaving, reload: () => load() };
};
